import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";

import { flags } from "@/config";
import { flightRecorder } from "@/services/debug/FlightRecorder";
import type { CollectionName, RowOf } from "@/store/collections";
import { reportUndeclaredReads, trackReads } from "@/store/readTracking";
import { store } from "@/store/store";

/**
 * Recomputes a derived value whenever any of the given collections commits a change, riding
 * TanStack DB's public change streams (`Collection.subscribeChanges`).
 *
 * The escape hatch for read models the live-query DSL cannot express yet — the driver roster,
 * the feed lists, and unread aggregation, whose pipelines span half a dozen collections with
 * bespoke search/sort/filter logic. Each call site declares the collections its compute reads;
 * everything else in the app reads through live queries, and these pipelines are the follow-up
 * candidates for decomposition into layered live queries.
 *
 * A collection the compute reads but doesn't declare is invisible to the version sum, so its
 * writes leave the memo stale. Debug mode tracks the reads and reports the undeclared ones.
 *
 * Every run is timed into the flight recorder under `debugName` (the collections list when absent),
 * with whether it returned a new reference — the signal that its consumers re-render.
 */
export function useCollectionsCompute<Result>(
  collections: readonly CollectionName[],
  compute: () => Result,
  deps: readonly unknown[],
  debugName?: string,
): Result {
  const collectionsKey = collections.join(",");
  const collectionsRef = useRef(collections);
  collectionsRef.current = collections;
  const previousRef = useRef<Result | undefined>(undefined);

  // Ride the store's own write-flush signal rather than per-collection `subscribeChanges`
  // streams: a change subscription attached during boot can go permanently silent for writes it
  // never observed (title/roster unreads stayed stale on live AppSync flips), while the flush
  // listener fires for every committed write and truncate. The version-sum snapshot below keeps
  // re-renders scoped to the watched collections — an unrelated flush reads an equal snapshot and
  // React bails out.
  const subscribe = useCallback((listener: () => void) => store.subscribeWrites(listener), []);
  // The snapshot is the store-side version sum (bumped synchronously by every write), not a
  // subscription-local counter: writes landing between render and the effect-phase subscribe are
  // caught by React's post-subscribe snapshot re-check.
  const currentVersion = useSyncExternalStore(
    subscribe,
    // biome-ignore lint/correctness/useExhaustiveDependencies: collectionsKey is the stable identity of the collections list
    useCallback(() => store.versionOf(collectionsRef.current), [collectionsKey]),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentVersion is the change signal; deps carry the compute closure's inputs
  return useMemo(() => {
    const name = debugName ?? collectionsKey;
    const startedAt = performance.now();
    const result = flags.debugMode ? computeTrackingReads(name, compute, collectionsRef.current) : compute();
    flightRecorder.recordCompute(name, startedAt, performance.now(), result !== previousRef.current);
    previousRef.current = result;
    return result;
  }, [currentVersion, collectionsKey, debugName, ...deps]);
}

/** Runs `compute` with read tracking and reports any collection it read without declaring. */
function computeTrackingReads<Result>(
  name: string,
  compute: () => Result,
  declared: readonly CollectionName[],
): Result {
  const { result, collections: reads } = trackReads(compute);
  reportUndeclaredReads(name, declared, reads);
  return result;
}

/**
 * Subscribes to a single row by key — the fine-grained path for hot cells (e.g. one driver's
 * availability). Re-renders only when the row's reference changes; identical snapshots
 * short-circuit the re-render.
 */
export function useCollectionRow<K extends CollectionName>(name: K, key: string | null | undefined): RowOf<K> | null {
  // Same write-flush signal as `useCollectionsCompute` (see above for why not `subscribeChanges`).
  // The row-reference snapshot keeps this fine-grained: writes replace a row object only when it
  // actually changed, so an untouched row returns an identical reference and React bails out.
  const subscribe = useCallback(
    (listener: () => void) => (key == null ? () => {} : store.subscribeWrites(listener)),
    [key],
  );
  return useSyncExternalStore(
    subscribe,
    useCallback(() => (key == null ? null : (store.get(name, key) ?? null)), [name, key]),
  );
}
